<template>
    <div v-if="project" class="project-view layout-max-width">
        <heading :title="title" tag="project-view-title">

            <b-button
                v-if="canAddRental"
                class="ml-3"
                variant="primary"
                :to="`/rental/create/projectId/${projectId}`">
                Add Rental
                <b-icon-person-plus/>
            </b-button>
            <b-button
                v-if="canAddSubs"
                class="ml-3"
                variant="primary"
                :to="`/project/addsub/${projectId}`">
                Add Subcontractor
                <b-icon-person-plus/>
            </b-button>
            <b-button
                v-if="canEdit"
                class="ml-3"
                variant="primary"
                :to="`/project/edit/${projectId}`">
                Edit
                <b-icon-pencil-square/>
            </b-button>
            <b-button
                v-if="canAudit"
                :disabled="project.status === 'DRAFT'"
                class="ml-3"
                variant="primary"
                :to="`/audit/create/${projectId}`">
                Start Audit
                <b-icon-plus-square/>
            </b-button>
            <b-button v-if="canClose && project.status !== 'CLOSED'"
                      :disabled="project.status === 'DRAFT'"
                      class="ml-3"
                      variant="danger"
                      @click="closeProject">
                Close
                <b-icon-bag-check/>
            </b-button>
            <b-button v-if="canClose && project.status === 'CLOSED'"
                      class="ml-3"
                      variant="primary"
                      @click="reopenProject">
                Re-Open
                <b-icon-arrow-counterclockwise/>
            </b-button>
            <b-button v-if="canDelete"
                      class="ml-3"
                      variant="danger"
                      @click="deleteProject">
                Delete
                <b-icon-dash-square/>
            </b-button>
            <template v-if="$user.isAdmin() || $user.isSuperAdmin()">
                <div class="project__export-buttons">
                    <export-button class="ml-3 mt-1" @export="exportCompanies">Export Companies</export-button>
                    <export-button class="ml-3 mt-1" :disabled="!(project.primeEquipment && project.subEquipment)" @export="exportEquipment">Export Equipment</export-button>
                    <export-button class="ml-3 mt-1" @export="exportRentals">Export Rentals</export-button>
                </div>
            </template>

        </heading>
        <b-row class="mb-4">
            <b-col class="col-12 col-lg-6">
                <view-table
                    :model="projectFields"
                    :data="project"
                    :fields="['id', 'name', 'agency', 'department', 'dollar_value', 'proceed_date', 'end_date', 'status', 'closed',
                    'projectManager', 'primeCompanyProjects', 'subCompanyProjects', 'rentals', 'notes']">
                    <template v-slot:status="slotProps">
                        {{ getStatusLabel(slotProps.value) }}
                        <b-button
                            v-if="slotProps.value === 'DRAFT' && canEdit"
                            @click="startProject"
                            variant="warning">
                            Start Project
                        </b-button>
                    </template>
                    <template v-slot:primeCompanyProjects="slotProps">
                        <company-project-link-list :company-projects="slotProps.value" collapse-key="collapse-primes"/>
                    </template>
                    <template v-slot:subCompanyProjects="slotProps">
                        <company-project-link-list :company-projects="slotProps.value" collapse-key="collapse-subs"/>
                    </template>
                    <template v-slot:projectManager="slotProps">
                        <div v-for="manager in slotProps.value" :key="manager.id" class="project-manager-list">
                            <user-details :user="manager"/>
                        </div>
                    </template>
                    <template v-slot:rentals="slotProps">
                        <rental-link-list :rentals="slotProps.value"/>
                    </template>
                    <template v-slot:dollar_value="slotProps">
                        ${{ slotProps.value }}
                    </template>
                    <template v-slot:proceed_date="slotProps">
                        <timestamp :date="slotProps.value" :include-year="true" :include-time="false"/>
                    </template>
                    <template v-slot:end_date="slotProps">
                        <timestamp :date="slotProps.value" :include-year="true" :include-time="false"/>
                    </template>
                    <template v-slot:closed="slotProps">
                        <timestamp :date="slotProps.value" :include-year="true" :include-time="false"/>
                    </template>
                    <template v-slot:agency="slotProps">
                        {{ slotProps.value.name }}
                    </template>
                </view-table>
            </b-col>
            <b-col>
                <map-selector
                    v-model="project.location"
                    :read-only="true"/>
            </b-col>
        </b-row>
        <b-row>
            <b-col class="col-12 col-md-6">
                <project-view-attestation
                    :project="project"/>
            </b-col>
            <b-col v-if="project && canEdit" class="col-12 col-md-6">
                <audit-list-widget
                    :audits="project.audits"/>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <project-invitations :project-id="projectId" :agency-id="project.agency_id" v-if="canEdit && project"></project-invitations>
            </b-col>
        </b-row>
        <attachment-list class="mt-2"
                         model-class="Project"
                         model-table="project"
                         help-tag="project-upload"
                         :model-id="projectId"
                         :readOnly="false"/>
    </div>
</template>

<script>
import Vue from 'vue'
import ProjectFields from '@/models/Project'
import Authorization from '@/providers/Authorization'
import MapSelector from '@/components/MapSelector'
import AuditListWidget from '@/components/AuditListWidget'
import CompanyProjectLinkList from '@/components/CompanyProjectLinkList'
import Export from "@/providers/Export"
import ProjectInvitations from "@/components/ProjectInvitations"
import * as _ from 'lodash'

export default Vue.extend({
    name: 'ProjectView',
    components: {ProjectInvitations, CompanyProjectLinkList, AuditListWidget, MapSelector },
    props: {
        projectId: Number,
    },
    data() {
        return {
            title: '',
            project: null,
            projectFields: ProjectFields,
            canEdit: false,
            canAudit: false,
            canClose: false,
            canDelete: false,
            canAddRental: false,
            canAddSubs: false,
        }
    },
    methods: {
        getStatusLabel(value) {
            return ProjectFields.getStatusLabel(value)
        },
        closeProject() {
            this.$api.closeProject(this, this.projectId).then((response) => {
                this.fetchProject()
            }, (error) => {
                this.$toasted.error('Could not close project')
            })
        },
        reopenProject() {
            this.$api.reopenProject(this, this.projectId).then((response) => {
                this.fetchProject()
            }, (error) => {
                this.$toasted.error('Could not re-open project')
            })
        },
        deleteProject() {
            this.$bvModal.msgBoxConfirm('Would you like to delete this project? All rentals, exemptions, and audits associated with this project will be deleted too. This action cannot be undone.')
                .then(async value => {
                    if (value) {
                        this.$api.deleteItem(this, 'project', this.project)
                            .then(() => {
                                this.$router.push('/project/list')
                                this.$toasted.info('Project deleted')
                            })
                            .catch(() => {
                                this.$toasted.error(`Could not delete project`)
                            })
                    }
                })
        },
        startProject() {
            this.$bvModal.msgBoxConfirm('This will set the project to "Entered but not Attested" Status until all equipment owners have attested. Proceed?')
                .then(value => {
                    const project = this.project
                    project.started = true
                    this.$api.updateItem(this, 'project', project).then(() => {
                        this.fetchProject()
                    })
                })
        },
        fetchProject() {
            this.$api.getItem(this, 'project', this.projectId, ['agency', 'primeCompanyProjects', 'subCompanyProjects', 'primeEquipment'])
                .then(async (data) => {
                    this.title = data.name
                    this.project = data
                    // Lazy load long subEquipment fetch and use set to cause reactive update on nested object
                    Vue.set(this.project, 'subEquipment', (await this.$api.getItem(this, 'project', this.projectId, ['subEquipment'])).subEquipment)
                })
        },
        exportCompanies(fileType) {
            let primes = this.project.primeCompanies
            let subs = this.project.subCompanies
            primes.forEach((p) => {
                p['company_type'] = 'Prime'
                const companyProject = this.project.primeCompanyProjects.find((cp) => cp.company_id === p.id)
                p['attested_date'] = companyProject.attested
            })
            subs.forEach((s) => {
                s['company_type'] = 'Sub'
                const companyProject = this.project.subCompanyProjects.find((cp) => cp.company_id === s.id)
                s['attested_date'] = companyProject.attested
            })
            const companies = primes.concat(subs)

            const companyFields = {
                name: 'name',
                company_type: 'company_type',
                attested_date: 'attested_date',
                ccb_number: 'ccb_number',
                alt_license_type: 'alt_license_type',
                alt_license_number: 'alt_license_number',
                url: 'url',
                status: 'status',
                equipmentCount: 'equipmentCount',
                companyUserCount: 'companyUserCount',
                companyProjectCount: 'companyProjectCount',
                auditNoIssueCount: 'auditNoIssueCount',
                auditIssueCount: 'auditIssueCount',
                auditViolationCount: 'auditViolationCount',
                auditResolvedCount: 'auditResolvedCount',
                totalExemptionCount: 'totalExemptionCount',
                pendingExemptionCount: 'pendingExemptionCount',
                draftExemptionCount: 'draftExemptionCount',
                grantedExemptionCount: 'grantedExemptionCount',
                deniedExemptionCount: 'deniedExemptionCount',
                mailing_address: 'mailing_address',
                city: 'city',
                state: 'state',
                zip: 'zip',
                cobid_certified: 'cobid_certified',
                cobid_number: 'cobid_number',
                cobid_type: 'cobid_type',
                contact_first_name: 'contact_first_name',
                contact_last_name: 'contact_last_name',
                contact_email: 'contact_email',
                contact_phone_office: 'contact_phone_office',
                contact_phone_mobile: 'contact_phone_mobile'
            }
            let callback = (field, value, record) => {
                return value
            }
            Export.exportRecords(companies, companyFields, 'Companies for ' + this.project.name, fileType, callback)
        },
        exportEquipment(fileType) {
            let primeEquip = this.project.primeEquipment
            let subEquip = this.project.subEquipment
            primeEquip.forEach((p) => {
                p['company_type'] = 'Prime'
            })
            subEquip.forEach((s) => {
                s['company_type'] = 'Sub'
            })
            const equipment = primeEquip.concat(subEquip)
            const equipmentFields = {
                company: 'Company Type',
                auditIssueCount: 'Audit # Issues',
                auditViolationCount: 'Audit # Resolved Count',
                auditResolvedCount: 'Audit # Resolved Issues/Violations',
                registrationNumber: 'Vehicle ID',
                type: 'Fuel Type',
                on_road: 'Cobid Certified',
                attested: 'EmissionTier',
                exemption_type: 'Exemption Granted Thru',

                // these must be updated yearly along with ComplianceService::$YEARS
                compliance_2024: 'Compliance 2024',
                compliance_2025: 'Compliance 2025',
                compliance_2026: 'Compliance 2026',
                compliance_2027: 'Compliance 2027',

                compliance_plan: 'Equipment Model Year',
                equipment_manufacturer: 'Equipment Model',
                equipment_serial_number: 'Engine Family Name',
                engine_manufacturer: 'Engine Model',
                engine_model_year: 'Engine Serial Number',
                engine_hp: 'Engine HP'
            }
            const callback = Export.equipmentCallback
            Export.exportRecords(equipment, equipmentFields, 'Equipment for ' + this.project.name, fileType, callback)
        },
        exportRentals(fileType) {
            const rentalCompanyFields = {
                name: 'name',
                email: 'email',
                address_1: 'address_1',
                address_2: 'address_2',
                city: 'city',
                state: 'state',
                zip: 'zip',
                phone: 'phone'
            }
            const rentalFields = {
                status: 'status',
                agreement_number: 'agreement_number',
            }
            const fields = {...rentalCompanyFields, ...rentalFields}
            let callback = (field, value, record) => {
                if(_.keys(rentalCompanyFields).includes(field) && record.rentalCompany) {
                    return record.rentalCompany[field]
                }
                return value
            }
            Export.exportRecords(this.project.rentals, fields, 'Rentals for ' + this.project.name, fileType, callback)
        }
    },
    created() {
        this.fetchProject()
        Authorization.can({action: 'edit', model: 'project', arg: this.projectId})
            .then((canEdit) => this.canEdit = canEdit)
        Authorization.can({action: 'create', model: 'audit'})
            .then((canAudit) => this.canAudit = canAudit)
        Authorization.can({action: 'close', model: 'project', arg: this.projectId})
            .then((canClose) => this.canClose = canClose)
        Authorization.can({action: 'delete', model: 'project'})
            .then((canDelete) => this.canDelete = canDelete)
        Authorization.can({action: 'add-subcontractors', model: 'project', arg: this.projectId})
            .then((canAddSubs) => this.canAddSubs = canAddSubs)
        Authorization.can({action: 'create', model: 'rental', arg: this.projectId})
            .then((canAddRental) => this.canAddRental = canAddRental)
    }
})
</script>

<style lang="scss">
  @media (min-width:1024px) {
      .project__export-buttons {
          margin-top:6px;
      }
  }
</style>
